'use client';

import Link from "next/link";
import { useEffect, useState } from "react";

interface ErrorScreenProps {
  code: string;
  label: string;
  title: string;
  message: string;
  terminal: string;
  showRetry?: boolean;
}

export function ErrorScreen({ 
  code, 
  label, 
  title, 
  message, 
  terminal,
  showRetry = false 
}: ErrorScreenProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <main className="fixed inset-0 min-h-screen w-full bg-[#000] font-mono text-white flex flex-col items-start justify-center px-6 md:px-20 overflow-hidden z-[9999]">
      {/* Brand Logo */}
      <div className="absolute top-8 left-6 md:left-20 z-20">
        <Link href="/" className="pointer-events-auto hover:opacity-80 transition-opacity flex items-center">
          <img src="/logo/Primary_Light_Color.svg" alt="DOTSLASHLAB" className="h-16 w-auto dark:hidden" />
          <img src="/logo/Primary_Dark_Color.svg" alt="DOTSLASHLAB" className="h-16 w-auto hidden dark:block" />
        </Link>
      </div>

      {/* Grid Overlay: 1px lines every 40px at ~1.5% opacity (subtle) */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.015] z-0" 
        style={{
          backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
          backgroundSize: "40px 40px"
        }}
      />

      {/* Background Decoration: Massive ghost code in near-black */}
      <div className="absolute bottom-[-5%] right-[-5%] text-[clamp(200px,40vw,600px)] font-black text-[#0a0a0a] leading-none pointer-events-none select-none z-0">
        {code.replace('/', '')}
      </div>

      <div className="relative z-10 w-full max-w-4xl space-y-10">
        {/* Label: green, tiny, small tracking */}
        <p className="text-[#7b6ff0] text-[10px] md:text-xs tracking-widest font-bold uppercase animate-pulse">
          {label}
        </p>

        {/* Heading: Massive bold, tight letter-spacing, green slash if present */}
        <h1 className="text-[clamp(80px,15vw,120px)] font-black leading-[0.9] tracking-tighter uppercase">
          {code.includes('/') ? (
            <>
              {code.split('/')[0]}<span className="text-[#7b6ff0]">/</span>{code.split('/')[1]}
            </>
          ) : (
            code
          )}
        </h1>

        {/* HR: 1px dark gray */}
        <div className="h-[1px] bg-[#222] w-full" />

        {/* Body Text */}
        <div className="space-y-4 max-w-2xl">
          <p className="text-[#a1a1aa] text-lg md:text-xl font-medium tracking-tight">
            {title}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.47)' }} className="text-base md:text-lg leading-relaxed">
            {message}
          </p>
        </div>

        {/* Terminal Line */}
        <div className="py-2">
          <p style={{ color: 'rgba(255,255,255,0.47)' }} className="text-sm md:text-base font-medium selection:bg-white/5">
            &gt; {terminal}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-6 pt-4">
          <Link 
            href="/"
            className="bg-[#7b6ff0] text-black px-10 py-5 font-black uppercase tracking-widest hover:bg-[#6559d4] transition-all duration-300 rounded-none text-center text-sm active:scale-95 whitespace-nowrap"
          >
            ← BACK TO BASE
          </Link>
          
          {showRetry && (
            <button 
              onClick={handleRetry}
              className="border border-[#222] text-white px-10 py-5 font-black uppercase tracking-widest hover:bg-white/5 transition-all duration-300 rounded-none text-center text-sm active:scale-95 whitespace-nowrap"
            >
              RETRY CONNECTION
            </button>
          )}

          {!showRetry && (
            <Link 
              href="/work"
              className="border border-[#222] text-white px-10 py-5 font-black uppercase tracking-widest hover:bg-white/5 transition-all duration-300 rounded-none text-center text-sm active:scale-95 whitespace-nowrap"
            >
              VIEW OUR WORK
            </Link>
          )}
        </div>
      </div>
    </main>
  );
}
